import { IpAddress, IpVersion } from "./IpAddress";
import { MacAddress } from "./MacAddress";
import { IpSubnet } from "./IpSubnet";
import { Dictionary } from "../Utils/Dictionary";
import { VirtualHostInfo } from "../InfoSets/VirtualHostInfo";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class PhysicalNic implements ObjectSerializer {

	public Key: string;
	public get HasKey(): boolean {
		return !!this.Key;
	}

	public HostID: string;
	public get HasHostID(): boolean {
		return !!this.HostID;
	}

	public Device: string;
	public get HasDevice(): boolean {
		return !!this.Device;
	}

	public Driver: string;
	public get HasDriver(): boolean {
		return !!this.Driver;
	}

	public PCI: string;
	public get HasPCI(): boolean {
		return !!this.PCI;
	}

	public IpAddress: IpAddress;
	public get HasIpAddress(): boolean {
		return this.IpAddress && this.IpAddress.IsValidDiscoveryAddress;
	}

	public SubnetMask:IpAddress;
	public get HasSubnetMask(): boolean {
		return !!this.SubnetMask;
	}

	public MacAddress:MacAddress;
	public get HasMacAddress(): boolean {
		return this.MacAddress && !this.MacAddress.IsNull;
	}

	public SpeedMb:number;
	public get HasSpeedMb(): boolean {
		return this.SpeedMb >= 0;
	}

	public Duplex:boolean;
	public IsDhcp:boolean;

	public get IpSubnet(): IpSubnet {
		var ipSubnet: IpSubnet = null;

		if (this.IpAddress.IpVersion == IpVersion.IPv4) {
			if (this.HasSubnetMask)
				ipSubnet = IpSubnet.CreateFromAddresses(this.IpAddress, this.SubnetMask);
		}

		return ipSubnet;
	}

	constructor(key?: string) {
		this.Key = key;

		this.Init();
	}

	private Init(): void {
		this.HostID = '';
		this.Device = '';
		this.Driver = '';
		this.PCI = '';

		this.IpAddress = null;
		this.SubnetMask = null;
		this.MacAddress = null;

		this.SpeedMb = -1;
		this.Duplex = false;
		this.IsDhcp = false;
	}

	public Write(obj: any, context: any): void {
		obj.physical_nic_entry = {};
		obj = obj.physical_nic_entry;

		obj.key = this.Key;

		if (this.HasHostID)
			obj.host_id = this.HostID;

		if (this.HasDevice)
			obj.pnic_device = this.Device;

		if (this.HasDriver)
			obj.pnic_driver = this.Driver;

		if (this.HasPCI)
			obj.pnic_pci = this.PCI;

		if (this.HasIpAddress)
			obj.ip_address = ValueEncoder.EncodeIpAddress(this.IpAddress);

		if (this.HasSubnetMask)
			obj.subnet_mask = ValueEncoder.EncodeIpAddress(this.SubnetMask);

		if (this.HasMacAddress)
			obj.mac_address = ValueEncoder.EncodeMacAddress(this.MacAddress);

		if (this.HasSpeedMb)
			obj.speed_mb = ValueEncoder.EncodeInteger(this.SpeedMb);

		// (sic)
		obj.suplex = ValueEncoder.EncodeBoolean(this.Duplex);

		obj.dhcp = ValueEncoder.EncodeBoolean(this.IsDhcp);
	}

	public Read(obj: any, context: any): void {
		if (!obj.physical_nic_entry)
			throw new Error();
		obj = obj.physical_nic_entry;

		if (obj.key !== undefined)
			this.Key = obj.key;

		if (obj.host_id !== undefined)
			this.HostID = obj.host_id;

		if (obj.pnic_device !== undefined)
			this.Device = obj.pnic_device;

		if (obj.pnic_driver != undefined)
			this.Driver = obj.pnic_driver;

		if (obj.pnic_pci !== undefined)
			this.PCI = obj.pnic_pci;

		if (obj.ip_address !== undefined)
			this.IpAddress = ValueEncoder.DecodeIpAddress(obj.ip_address);

		if (obj.subnet_mask !== undefined)
			this.SubnetMask = ValueEncoder.DecodeIpAddress(obj.subnet_mask);

		if (obj.mac_address !== undefined)
			this.MacAddress = ValueEncoder.DecodeMacAddress(obj.mac_address);

		if (obj.speed_mb !== undefined)
			this.SpeedMb = ValueEncoder.DecodeInteger(obj.speed_mb);

		// (sic)
		if (obj.suplex !== undefined)
			this.Duplex = ValueEncoder.DecodeBoolean(obj.suplex);

		if (obj.dhcp !== undefined)
			this.IsDhcp = ValueEncoder.DecodeBoolean(obj.dhcp);
	}
}

export class PhysicalNicTable implements ObjectSerializer {

	private _pnicTable: Dictionary<string, PhysicalNic>;

	public VirtualHost: VirtualHostInfo
	public get HasVirtualHost(): boolean {
		return this.VirtualHost && this.VirtualHost.HasHostID;
	}

	constructor(parentHost: VirtualHostInfo ) {
		this._pnicTable = new Dictionary<string, PhysicalNic>();
		this.VirtualHost = parentHost;
	}

	public Add(pnicKey: string): PhysicalNic {
		var vNic: PhysicalNic = null;

		if (this._pnicTable.ContainsKey(pnicKey)) {
			vNic = this._pnicTable.Item(pnicKey);
		}
		else {
			vNic = new PhysicalNic(pnicKey);
			this._pnicTable.Add(pnicKey, vNic);
		}

		if (this.HasVirtualHost)
			vNic.HostID = this.VirtualHost.HostID;

		return vNic;
	}

	public AddNic(pnicEntry: PhysicalNic)	{
		// REMOVE any existing entry
		if (this._pnicTable.ContainsKey(pnicEntry.Key))
			this._pnicTable.Remove(pnicEntry.Key);

		// ADD
		this._pnicTable.Add(pnicEntry.Key, pnicEntry);

		// ASSOCIATE to the VirtualHost
		if (this.HasVirtualHost)
			pnicEntry.HostID = this.VirtualHost.HostID;
	}

	public Clear(): void {
		this._pnicTable.Clear();
	}

	public get Count():number {
		return this._pnicTable.Count;
	}

	public Remove(pnicKey: string ):boolean {
		return this._pnicTable.Remove(pnicKey);
	}

	public ForEach(callbackfn: (value: PhysicalNic, index: number, array: PhysicalNic[]) => void, thisArg?: any): void {
		this._pnicTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: PhysicalNic, index: number, array: PhysicalNic[]) => boolean, thisArg?: any): boolean {
		return this._pnicTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: PhysicalNic, index: number, array: PhysicalNic[]) => boolean, thisArg?: any): boolean {
		return this._pnicTable.Values().Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.physical_nic_table = {};
		obj = obj.physical_nic_table;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._pnicTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.physical_nic_table)
			throw new Error();
		obj = obj.physical_nic_table;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new PhysicalNic();
				entry.Read(value, context);
				this.AddNic(entry);
			}, this);
		}
	}
}
